import {
  DriverPlanListEntry,
  DriverPlanListEntryCheckoutEvent,
  DriverPlanListEntryEndNode,
  DriverPlanListEntrySolvedOrder,
  DriverPlanListEntryStartNode,
} from "../../../../../../../common/services/driver/plan/list/driver-plan-list";
import DriverDetailsDriverPlanEntry, {
  DriverDetailsDriverPlanEntryNodeExclusions,
  DriverDetailsDriverPlanEntryWaypoint,
  DriverDetailsDriverPlanEntryWaypointType,
} from "../../common/types/driver-details-plan-entry";

const createWaypointForStartNode = (
  responseStartNode: DriverPlanListEntryStartNode,
  responseCheckoutEvents: DriverPlanListEntryCheckoutEvent[],
  stageCounter: number,
): DriverDetailsDriverPlanEntryWaypoint => {
  const responseCheckoutEvent = responseCheckoutEvents.find(
    (item) => item.nodeId === responseStartNode.meta.id,
  );

  const waypoint: DriverDetailsDriverPlanEntryWaypoint = {
    type: DriverDetailsDriverPlanEntryWaypointType.DRIVER_HOME,
    uuid: responseStartNode.meta.id,
    address: {
      displayName: responseStartNode.displayName,
      latitude: responseStartNode.lat,
      longitude: responseStartNode.lon,
    },
    checkoutDate: responseCheckoutEvent?.achievedAt
      ? new Date(responseCheckoutEvent.achievedAt)
      : null,
    createdAt: responseCheckoutEvent?.createdAt ?? null,
    createdByName: responseCheckoutEvent?.createdByName ?? null,
    clientDisplayName: "",
    estimatedDate: new Date(responseStartNode.meta.estimatedTime),
    plannedHaltingTime: responseStartNode.haltingTime,
    forcedHaltingTime: responseCheckoutEvent?.haltingTime ?? null,
    highwayCost: responseCheckoutEvent?.highwayCharge ?? null,
    order: null,
    plannedDate: responseStartNode.time
      ? new Date(responseStartNode.time)
      : null,
    isCheckouted: !!responseCheckoutEvent,
    checkoutEventUuid: responseCheckoutEvent?.id ?? null,
    isCheckoutManagementEnabled: true,
    solvedOrderUuid: null,
    stageNo: stageCounter,
  };

  return waypoint;
};

const createWaypointForEndNode = (
  responseEndNode: DriverPlanListEntryEndNode,
  responseCheckoutEvents: DriverPlanListEntryCheckoutEvent[],
  stageCounter: number,
): DriverDetailsDriverPlanEntryWaypoint => {
  const responseCheckoutEvent = responseCheckoutEvents.find(
    (item) => item.nodeId === responseEndNode.meta.id,
  );

  const waypoint: DriverDetailsDriverPlanEntryWaypoint = {
    type: DriverDetailsDriverPlanEntryWaypointType.DRIVER_HOME,
    uuid: responseEndNode.meta.id,
    address: {
      displayName: responseEndNode.displayName,
      latitude: responseEndNode.lat,
      longitude: responseEndNode.lon,
    },
    checkoutDate: responseCheckoutEvent?.achievedAt
      ? new Date(responseCheckoutEvent.achievedAt)
      : null,
    createdAt: responseCheckoutEvent?.createdAt ?? null,
    createdByName: responseCheckoutEvent?.createdByName ?? null,
    clientDisplayName: "",
    estimatedDate: new Date(responseEndNode.meta.estimatedTime),
    plannedHaltingTime: responseEndNode.haltingTime,
    forcedHaltingTime: responseCheckoutEvent?.haltingTime ?? null,
    highwayCost: responseCheckoutEvent?.highwayCharge ?? null,
    order: null,
    plannedDate: responseEndNode.time ? new Date(responseEndNode.time) : null,
    isCheckouted: !!responseCheckoutEvent,
    checkoutEventUuid: responseCheckoutEvent?.id ?? null,
    isCheckoutManagementEnabled: true,
    solvedOrderUuid: null,
    stageNo: stageCounter,
  };

  return waypoint;
};

const createWaypointsForSolvedOrder = (
  responseSolvedOrder: DriverPlanListEntrySolvedOrder,
  responseCheckoutEvents: DriverPlanListEntryCheckoutEvent[],
  stageCounter: number,
  onComplete: (stageCounterValue: number) => void,
): DriverDetailsDriverPlanEntryWaypoint[] => {
  const waypoints: DriverDetailsDriverPlanEntryWaypoint[] = [];

  let internalStageCounter = stageCounter;

  responseSolvedOrder.ride.seq.forEach((seqItem) => {
    const responseCheckoutEvent = responseCheckoutEvents.find(
      (item) => item.nodeId === seqItem.meta.id,
    );

    const waypoint: DriverDetailsDriverPlanEntryWaypoint = {
      type: DriverDetailsDriverPlanEntryWaypointType.ORDER_WAYPOINT,
      uuid: seqItem.meta.id,
      clientDisplayName: responseSolvedOrder.cargoCompany.displayName,
      order: {
        uuid: responseSolvedOrder.transportingOrder.cargoOrder.id,
        cargoInternalOrderId:
          responseSolvedOrder.transportingOrder.cargoOrder.humanId,
      },
      address: {
        displayName: seqItem.displayName,
        latitude: seqItem.lat,
        longitude: seqItem.lon,
      },
      checkoutDate: responseCheckoutEvent?.achievedAt
        ? new Date(responseCheckoutEvent.achievedAt)
        : null,
      createdAt: responseCheckoutEvent?.createdAt ?? null,
      createdByName: responseCheckoutEvent?.createdByName ?? null,
      estimatedDate: new Date(seqItem.meta.estimatedTime),
      plannedHaltingTime: seqItem.haltingTime,
      forcedHaltingTime: responseCheckoutEvent?.haltingTime ?? null,
      highwayCost: responseCheckoutEvent?.highwayCharge ?? null,
      plannedDate: seqItem.time ? new Date(seqItem.time) : null,
      isCheckouted: !!responseCheckoutEvent,
      checkoutEventUuid: responseCheckoutEvent?.id ?? null,
      isCheckoutManagementEnabled: true,
      solvedOrderUuid: responseSolvedOrder.id,
      stageNo: internalStageCounter,
    };

    waypoints.push(waypoint);
    internalStageCounter++;
  });

  onComplete(internalStageCounter);

  return waypoints;
};

const createWaypointsForSolvedOrders = (
  responseSolvedOrders: DriverPlanListEntrySolvedOrder[],
  responseCheckoutEvents: DriverPlanListEntryCheckoutEvent[],
  initialStageCounter: number,
): DriverDetailsDriverPlanEntryWaypoint[] => {
  let stageCounter = initialStageCounter;
  const allWaypoints: DriverDetailsDriverPlanEntryWaypoint[] = [];

  responseSolvedOrders.forEach((responseSolvedOrder) => {
    const orderWaypoints = createWaypointsForSolvedOrder(
      responseSolvedOrder,
      responseCheckoutEvents,
      stageCounter,
      (updatedStageCounter) => {
        stageCounter = updatedStageCounter;
      },
    );

    allWaypoints.push(...orderWaypoints);
  });

  return allWaypoints;
};

const createPlanEntryWaypoints = (
  responsePlanEntry: DriverPlanListEntry,
): DriverDetailsDriverPlanEntryWaypoint[] => {
  const waypoints: DriverDetailsDriverPlanEntryWaypoint[] = [];

  let stageCounter: number = 1;

  const startWaypoint = createWaypointForStartNode(
    responsePlanEntry.startNode,
    responsePlanEntry.checkoutEvents,
    stageCounter,
  );

  stageCounter++;

  waypoints.push(startWaypoint);

  const orderWaypoints = createWaypointsForSolvedOrders(
    responsePlanEntry.solvedOrders,
    responsePlanEntry.checkoutEvents,
    stageCounter,
  );

  waypoints.push(...orderWaypoints);

  stageCounter = stageCounter = waypoints[waypoints.length - 1].stageNo + 1;

  const endWaypoint = createWaypointForEndNode(
    responsePlanEntry.endNode,
    responsePlanEntry.checkoutEvents,
    stageCounter,
  );

  waypoints.push(endWaypoint);

  return waypoints;
};

const createNodeExclusions = (
  responsePlanEntry: DriverPlanListEntry,
): DriverDetailsDriverPlanEntryNodeExclusions[] => {
  const exclusionList = responsePlanEntry.nodeExclusions.exclusions.map(
    (nodeExclusions) => ({
      approaching: nodeExclusions.excludeApproaching,
      inside: nodeExclusions.excludeInside,
      returning: nodeExclusions.excludeReturning,
      nodeIds: nodeExclusions.nodeIds,
    }),
  );

  return exclusionList;
};

const createPlanEntry = (
  responsePlanEntry: DriverPlanListEntry,
  isAdmin: boolean,
): DriverDetailsDriverPlanEntry => {
  const waypoints = createPlanEntryWaypoints(responsePlanEntry);
  const nodeExclusions = createNodeExclusions(responsePlanEntry);

  return {
    isHidden:
      !isAdmin &&
      waypoints.every(
        (waypoint) =>
          !waypoint.order || waypoint.order?.cargoInternalOrderId === null,
      ),
    isAnonymized: false,
    uuid: responsePlanEntry.id,
    id: responsePlanEntry.humanId,
    waypoints,
    nodeExclusions: nodeExclusions,
  };
};

const createPlanEntries = (
  responsePlanEntries: DriverPlanListEntry[],
  isAdmin: boolean,
): DriverDetailsDriverPlanEntry[] => {
  return responsePlanEntries.map((planEntryResponse) =>
    createPlanEntry(planEntryResponse, isAdmin),
  );
};

const driverDetailsActiveRoutePlansByRailyPlanEntryFactory = {
  createPlanEntries,
};

export default driverDetailsActiveRoutePlansByRailyPlanEntryFactory;
